import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FaBuilding,
  FaProjectDiagram,
  FaUsers,
  FaTasks,
  FaSitemap,
  FaUsersCog,
} from "react-icons/fa"; // Importing icons
import "../style/dashboard.css"; // Import CSS for styling
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
// Premium Vibrant Status Colors
const COLORS = ["#ff4d4d", "#ffa500", "#3b82f6", "#2ecc71", "#c0392b", "#845EC2"];

const Admindashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error] = useState(null);
  const [, setDashboardData] = useState([]);
  const [comapnys, setCompanys] = useState(0);
  const [projects, setProjects] = useState(0);
  const [employees, setEmployees] = useState(0);
  const [tasks, setTasks] = useState(0);
  const [teams, setTeams] = useState(0);
  const [departments, setDepartments] = useState(0);
  const [employeeRolesData, setEmployeeRolesData] = useState([]);
  const [taskStatusData, setTaskStatusData] = useState([]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [
        companysData,
        projectsData,
        employeesData,
        tasksData,
        teamsData,
        departmentsData
      ] = await Promise.all([
        axios.get("/api/company"),
        axios.get("/api/projects"),
        axios.get("/api/emp"),
        axios.get("/api/tasks"),
        axios.get("/api/teams"),
        axios.get("/api/dept")
      ]);

      const companyCount = companysData.data.company.length || 0;
      const projectCount = projectsData.data.project.length || 0;
      const employeeList = employeesData.data.employees || [];
      const employeeCount = employeeList.length || 0;
      const taskList = tasksData.data.task || [];
      const taskCount = taskList.length || 0;
      const teamCount = teamsData.data.team.length || 0;
      const departmentCount = departmentsData.data.department.length || 0;

      // Set raw counts
      setCompanys(companyCount);
      setProjects(projectCount);
      setEmployees(employeeCount);
      setTasks(taskCount);
      setTeams(teamCount);
      setDepartments(departmentCount);

      // 1. Calculate Task Status Distribution
      const statusCounts = {
        completed: 0,
        pending: 0,
        ongoing: 0,
        inreview: 0,
      };
      taskList.forEach((task) => {
        const status = task.project_task_status?.toLowerCase();
        if (statusCounts.hasOwnProperty(status)) {
          statusCounts[status]++;
        }
      });
      const taskPieData = [
        { name: "Pending", value: statusCounts.pending },
        { name: "Ongoing", value: statusCounts.ongoing },
        { name: "InReview", value: statusCounts.inreview },
        { name: "Completed", value: statusCounts.completed },
      ];
      setTaskStatusData(taskPieData);

      // 2. Calculate Employee Role Distribution
      const managers = employeeList.filter((emp) => emp.employee_role === "M").length;
      const teamLeaders = employeeList.filter((emp) => emp.employee_role === "T").length;
      const employees = employeeList.filter((emp) => emp.employee_role === "E").length;
      const rolePieData = [
        { name: "Managers", value: managers },
        { name: "Team Leaders", value: teamLeaders },
        { name: "Employees", value: employees },
      ];
      setEmployeeRolesData(rolePieData);

      // 3. Calculate Overall Distribution Pie Data
      const pieData = [
        { name: "Companies", value: companyCount },
        { name: "Departments", value: departmentCount },
        { name: "Projects", value: projectCount },
        { name: "Employees", value: employeeCount },
        { name: "Teams", value: teamCount },
        { name: "Tasks", value: taskCount },
      ];
      setDashboardData(pieData);

    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (loading) return <p className="loading">Loading...</p>;
  if (error) return <p className="error">{error}</p>;

  return (
    <div className="dashboard-main">
      <div className="dashboard">
        <div className="card total-companies">
          <div className="icon-bg"><FaBuilding /></div>
          <div className="card-text">
            <h3>Companies</h3>
            <p>{comapnys}</p>
          </div>
        </div>
        <div className="card total-departments">
          <div className="icon-bg"><FaSitemap /></div>
          <div className="card-text">
            <h3>Departments</h3>
            <p>{departments}</p>
          </div>
        </div>
        <div className="card total-projects">
          <div className="icon-bg"><FaProjectDiagram /></div>
          <div className="card-text">
            <h3>Projects</h3>
            <p>{projects}</p>
          </div>
        </div>
        <div className="card total-employees">
          <div className="icon-bg"><FaUsers /></div>
          <div className="card-text">
            <h3>Total Employees</h3>
            <p>{employees}</p>
          </div>
        </div>
        <div className="card total-teams">
          <div className="icon-bg"><FaUsersCog /></div>
          <div className="card-text">
            <h3>Teams</h3>
            <p>{teams}</p>
          </div>
        </div>
        <div className="card total-tasks">
          <div className="icon-bg"><FaTasks /></div>
          <div className="card-text">
            <h3>Tasks</h3>
            <p>{tasks}</p>
          </div>
        </div>
      </div>
      <div className="chart-main-container">
        <div className="chart-container">
          <h3>Employee Role Distribution</h3>
          <ResponsiveContainer height={420}>
            <PieChart>
              <Pie
                data={employeeRolesData}
                cx="50%"
                cy="45%"
                labelLine={false}
                innerRadius={82}
                outerRadius={105}
                paddingAngle={4}
                dataKey="value"
                stroke="none"
                label={({ cx, cy, midAngle, innerRadius, outerRadius, value, name, percent }) => {
                  if (value === 0) return null;
                  const radius = outerRadius + 25;
                  const x = cx + radius * Math.cos(-midAngle * (Math.PI / 180));
                  const y = cy + radius * Math.sin(-midAngle * (Math.PI / 180));

                  const lineX1 = cx + outerRadius * Math.cos(-midAngle * (Math.PI / 180));
                  const lineY1 = cy + outerRadius * Math.sin(-midAngle * (Math.PI / 180));
                  const lineX2 = cx + (outerRadius + 20) * Math.cos(-midAngle * (Math.PI / 180));
                  const lineY2 = cy + (outerRadius + 20) * Math.sin(-midAngle * (Math.PI / 180));

                  return (
                    <g>
                      <line x1={lineX1} y1={lineY1} x2={lineX2} y2={lineY2} stroke="#94a3b8" strokeWidth={1} />
                      <text x={x} y={y - 8} fill="#ffffff" textAnchor={x > cx ? "start" : "end"} style={{ fontSize: "13px", fontWeight: "600" }}>
                        {`${(percent * 100).toFixed(0)}%`}
                      </text>
                      <text x={x} y={y + 10} fill="#94a3b8" textAnchor={x > cx ? "start" : "end"} style={{ fontSize: "10px", fontWeight: "500", textTransform: 'uppercase' }}>
                        {`${name} (${value})`}
                      </text>
                    </g>
                  );
                }}
              >
                {employeeRolesData.map((entry, index) => (
                  <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <text x="50%" y="45%" textAnchor="middle" dominantBaseline="middle">
                <tspan x="50%" dy="0" fill="#94a3b8" style={{ fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1.5px' }}>
                  TOTAL ROLES
                </tspan>
                <tspan x="50%" dy="35" fill="#ffffff" style={{ fontSize: '38px', fontWeight: '800' }}>
                  {employeeRolesData.reduce((acc, curr) => acc + curr.value, 0)}
                </tspan>
              </text>
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(15, 23, 42, 0.95)',
                  borderColor: 'rgba(255,255,255,0.1)',
                  borderRadius: '10px',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.5)'
                }}
                itemStyle={{ color: '#ffffff', fontSize: '14px', fontWeight: '500' }}
              />
              <Legend
                verticalAlign="bottom"
                height={36}
                wrapperStyle={{ paddingTop: "20px" }}
                iconType="circle"
                formatter={(value) => <span style={{ color: '#e2e8f0', fontSize: '13px' }}>{value}</span>}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="chart-container">
          <h3>Task Status Distribution</h3>
          <ResponsiveContainer height={420}>
            <PieChart>
              <Pie
                data={taskStatusData}
                cx="50%"
                cy="45%"
                innerRadius={82}
                outerRadius={105}
                paddingAngle={4}
                dataKey="value"
                stroke="none"
                labelLine={false}
                label={({ cx, cy, midAngle, innerRadius, outerRadius, value, name, percent }) => {
                  if (value === 0) return null;
                  const radius = outerRadius + 25;
                  const x = cx + radius * Math.cos(-midAngle * (Math.PI / 180));
                  const y = cy + radius * Math.sin(-midAngle * (Math.PI / 180));

                  const lineX1 = cx + outerRadius * Math.cos(-midAngle * (Math.PI / 180));
                  const lineY1 = cy + outerRadius * Math.sin(-midAngle * (Math.PI / 180));
                  const lineX2 = cx + (outerRadius + 20) * Math.cos(-midAngle * (Math.PI / 180));
                  const lineY2 = cy + (outerRadius + 20) * Math.sin(-midAngle * (Math.PI / 180));

                  return (
                    <g>
                      <line x1={lineX1} y1={lineY1} x2={lineX2} y2={lineY2} stroke="#94a3b8" strokeWidth={1} />
                      <text x={x} y={y - 8} fill="#ffffff" textAnchor={x > cx ? "start" : "end"} style={{ fontSize: "13px", fontWeight: "600" }}>
                        {`${(percent * 100).toFixed(0)}%`}
                      </text>
                      <text x={x} y={y + 10} fill="#94a3b8" textAnchor={x > cx ? "start" : "end"} style={{ fontSize: "10px", fontWeight: "500", textTransform: 'uppercase' }}>
                        {`${name} (${value})`}
                      </text>
                    </g>
                  );
                }}
              >
                {taskStatusData.map((entry, index) => (
                  <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <text x="50%" y="45%" textAnchor="middle" dominantBaseline="middle">
                <tspan x="50%" dy="0" fill="#94a3b8" style={{ fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1.5px' }}>
                  TOTAL TASKS
                </tspan>
                <tspan x="50%" dy="35" fill="#ffffff" style={{ fontSize: '38px', fontWeight: '800' }}>
                  {taskStatusData.reduce((acc, curr) => acc + curr.value, 0)}
                </tspan>
              </text>
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(15, 23, 42, 0.95)',
                  borderColor: 'rgba(255,255,255,0.1)',
                  borderRadius: '10px',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.5)'
                }}
                itemStyle={{ color: '#ffffff', fontSize: '14px', fontWeight: '500' }}
              />
              <Legend
                verticalAlign="bottom"
                height={36}
                wrapperStyle={{ paddingTop: "20px" }}
                iconType="circle"
                formatter={(value) => <span style={{ color: '#e2e8f0', fontSize: '13px' }}>{value}</span>}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
export default Admindashboard;
